import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TachographCompanyCardGrid } from "./components/TachographCompanyCardGrid";
import { TachographDownloadsPanel } from "./components/TachographDownloadsPanel";
import { TachographDriverGrid } from "./components/TachographDriverGrid";
import { TachographSummaryCards } from "./components/TachographSummaryCards";
import { TachographVehicleGrid } from "./components/TachographVehicleGrid";
import { useDriverAssignments } from "./hooks/useDriverAssignments";
import { useTachographDeviceInfo } from "./hooks/useTachographDeviceInfo";
import { useTachographFileDownload } from "./hooks/useTachographFileDownload";
import { useTachographPolicy } from "./hooks/useTachographPolicy";
import { useTachographSignals } from "./hooks/useTachographSignals";
import {
  TACHOGRAPH_TABS,
  TachographManufacturer,
  TachographSummaryFilter,
  TachographTab,
  TachographUnitLabel,
} from "./models/Tachograph";
import { buildCompanyCardRows } from "./utilities/tachographCompanyCards";
import { buildDownloadRows, latestFileByEntityId } from "./utilities/tachographFiles";
import { buildDriverRows, buildVehicleRows } from "./utilities/tachographRows";
import { useDevicesBySearch } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useTachographCompanyCards } from "@/hooks/tachograph/useTachographCompanyCards";
import { useTachographDataFiles } from "@/hooks/tachograph/useTachographDataFiles";
import { useUsersBySearch } from "@/hooks/users/useUsersBySearch";

export function TachographPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TachographTab>("status");
  const [summaryFilter, setSummaryFilter] = useState<TachographSummaryFilter>("all");
  const [search, setSearch] = useState("");

  /** A counter narrows down the vehicle list, which only the status tab shows in full. */
  const selectSummaryFilter = (filter: TachographSummaryFilter) => {
    setSummaryFilter(current => (current === filter ? "all" : filter));
    setActiveTab("status");
  };

  /** Switching tabs drops the counter filter so two filters never stack unseen. */
  const selectTab = (tab: TachographTab) => {
    setActiveTab(tab);
    setSummaryFilter("all");
  };

  const translateCount = useCallback(
    (key: string, options: { count: number }) => t(key, options),
    [t],
  );

  const { data: devices = [], isLoading: isLoadingDevices } = useDevicesBySearch(search, false);
  const { data: allDevices = [], isLoading: isLoadingAllDevices } = useDevicesBySearch("", false);

  const deviceIds = useMemo(() => devices.map(device => device.id), [devices]);
  const allDeviceIds = useMemo(() => allDevices.map(device => device.id), [allDevices]);

  const { driverIdByDeviceId, deviceIdByDriverId, driverById, isLoading: isLoadingAssignments } =
    useDriverAssignments(allDeviceIds);

  const { data: drivers = [], isLoading: isLoadingDrivers } = useUsersBySearch(
    search,
    true,
    false,
    false,
    activeTab === "driver",
  );
  const driverIds = useMemo(() => drivers.map(driver => driver.id), [drivers]);

  const formatManufacturer = useCallback(
    (manufacturer: TachographManufacturer) => t(`tachograph.manufacturers.${manufacturer}`),
    [t],
  );

  const { policy, isLoading: isLoadingPolicy } = useTachographPolicy();
  const signalByDeviceId = useTachographSignals(deviceIds);
  const infoByDeviceId = useTachographDeviceInfo(deviceIds);
  const { data: files, isLoading: isLoadingFiles } = useTachographDataFiles(deviceIds, driverIds);
  const { data: companyCards = [], isLoading: isLoadingCompanyCards } = useTachographCompanyCards();
  const { downloadFiles, isDownloading } = useTachographFileDownload();

  const fileByDeviceId = useMemo(
    () => latestFileByEntityId(files?.deviceFiles ?? [], file => file.device?.id),
    [files],
  );
  const fileByDriverId = useMemo(
    () => latestFileByEntityId(files?.driverFiles ?? [], file => file.driver?.id),
    [files],
  );

  const unitsByDeviceId = useMemo(
    () =>
      new Map<string, TachographUnitLabel>(
        devices.map(device => [
          device.id,
          { name: device.name, licensePlate: getDeviceLicensePlate(device) },
        ]),
      ),
    [devices],
  );

  const licensePlateByDeviceId = useMemo(
    () => new Map(allDevices.map(device => [device.id, getDeviceLicensePlate(device)])),
    [allDevices],
  );

  const vehicleRows = useMemo(
    () =>
      buildVehicleRows({
        devices,
        driverIdByDeviceId,
        driverById,
        fileByDeviceId,
        signalByDeviceId,
        infoByDeviceId,
        formatManufacturer,
        intervalDays: policy.deviceIntervalDays,
        language: i18n.language,
        now: new Date(),
        t: translateCount,
      }),
    [
      devices,
      driverIdByDeviceId,
      driverById,
      fileByDeviceId,
      signalByDeviceId,
      infoByDeviceId,
      formatManufacturer,
      policy.deviceIntervalDays,
      i18n.language,
      translateCount,
    ],
  );

  const driverRows = useMemo(
    () =>
      buildDriverRows({
        drivers,
        deviceIdByDriverId,
        licensePlateByDeviceId,
        fileByDriverId,
        signalByDeviceId,
        intervalDays: policy.driverIntervalDays,
        language: i18n.language,
        now: new Date(),
        t: translateCount,
      }),
    [
      drivers,
      deviceIdByDriverId,
      licensePlateByDeviceId,
      fileByDriverId,
      signalByDeviceId,
      policy.driverIntervalDays,
      i18n.language,
      translateCount,
    ],
  );

  const downloadRows = useMemo(
    () => buildDownloadRows(files?.deviceFiles ?? [], unitsByDeviceId),
    [files, unitsByDeviceId],
  );

  const companyCardRows = useMemo(
    () =>
      buildCompanyCardRows(companyCards, {
        search,
        language: i18n.language,
        formatStatus: status => t(`tachograph.companyCardStatuses.${status}`),
      }),
    [companyCards, i18n.language, search, t],
  );

  const visibleVehicleRows = useMemo(() => {
    if (activeTab !== "status") return vehicleRows.filter(row => row.category === activeTab);

    switch (summaryFilter) {
      case "downloads":
        return vehicleRows.filter(row => row.downloadStatus !== "ok");
      case "inspections":
        return vehicleRows.filter(row => row.inspectionStatus !== "ok");
      case "connectors":
        return vehicleRows.filter(row => row.connectorStatus !== "online");
      default:
        return vehicleRows;
    }
  }, [activeTab, summaryFilter, vehicleRows]);

  const summary = useMemo(
    () => ({
      total: vehicleRows.length,
      downloadsDue: vehicleRows.filter(row => row.downloadStatus !== "ok").length,
      inspectionsDue: vehicleRows.filter(row => row.inspectionStatus !== "ok").length,
      connectorsIssue: vehicleRows.filter(row => row.connectorStatus !== "online").length,
    }),
    [vehicleRows],
  );

  const isLoadingVehicles =
    isLoadingDevices || isLoadingAllDevices || isLoadingAssignments || isLoadingFiles || isLoadingPolicy;

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0, boxSizing: "border-box" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("tachograph.nativeTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("tachograph.nativeDescription")}
        </Typography>
      </Box>

      <TachographSummaryCards
        summary={summary}
        activeFilter={summaryFilter}
        onSelect={selectSummaryFilter}
      />

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1, minHeight: 0, display: "flex" }}>
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Tabs
              value={activeTab}
              onChange={(_event, value: TachographTab) => selectTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TACHOGRAPH_TABS.map(tab => (
                <Tab key={tab} value={tab} label={t(`tachograph.tabs.${tab}`)} />
              ))}
            </Tabs>

            <TextField
              size="small"
              label={t("tachograph.search")}
              value={search}
              onChange={event => setSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />
          </Stack>

          <Box sx={{ flex: 1, minHeight: 520 }}>
            {activeTab === "driver" ? (
              <TachographDriverGrid
                rows={driverRows}
                loading={isLoadingDrivers || isLoadingAssignments || isLoadingFiles || isLoadingPolicy}
              />
            ) : activeTab === "companyCards" ? (
              <TachographCompanyCardGrid rows={companyCardRows} loading={isLoadingCompanyCards} />
            ) : activeTab === "downloads" ? (
              <TachographDownloadsPanel
                rows={downloadRows}
                loading={isLoadingDevices || isLoadingFiles}
                isDownloading={isDownloading}
                onDownload={downloadFiles}
              />
            ) : (
              <TachographVehicleGrid rows={visibleVehicleRows} loading={isLoadingVehicles} />
            )}
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
